import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CButton, CButtonGroup } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCart, cilLockLocked } from "@coreui/icons";
import type { AddOnDto } from "../../api/hooks/useAddOns";
import type { TradingViewAccountDto } from "../../api/hooks/useTradingViewAccount";
import { annualSavingsPct, type BillingCycle } from "../../api/hooks/useTradingStrategies";
import { useToast } from "../../context/useToast";
import { formatPriceUsd } from "../../lib/money";
import { addOnDetailsPath, routePaths } from "../../routes/route-paths";
import { type AddOnCartItem, useCartStore } from "../../state/cartStore";
import { addOnCycles, addOnPrice } from "./add-on-state";

/**
 * How to buy an add-on, laid out as a membership's: the billing cycle, the price for it, then Add to cart. Sold on
 * one cycle only, it has no cycle to choose. Once it's in the cart the cycle still changes it there.
 */
export function AddOnBuyPanel({
  addOn,
  tradingViewAccount,
}: {
  addOn: AddOnDto;
  tradingViewAccount: TradingViewAccountDto | null;
}) {
  const { t } = useTranslation("addOns");
  const { showSuccess } = useToast();
  const cartLine = useCartStore((s) => s.addOnItems.find((item) => item.addOnId === addOn.id));
  const addAddOn = useCartStore((s) => s.addAddOn);
  const removeAddOn = useCartStore((s) => s.removeAddOn);
  const updateBillingCycle = useCartStore((s) => s.updateAddOnBillingCycle);
  // An upgrade or an extension checks out on its own, so nothing else can join the cart beside one.
  const cartHeldBySomethingElse = useCartStore((s) => s.upgrade !== null || s.extension !== null);
  const cycles = addOnCycles(addOn);
  const [pendingCycle, setPendingCycle] = useState<BillingCycle>(cycles[0] ?? "Monthly");

  const cycle = cartLine?.billingCycle ?? pendingCycle;
  const price = addOnPrice(addOn, cycle);
  const savings =
    cycles.length > 1
      ? annualSavingsPct({ monthlyPriceUsd: addOn.monthlyPriceUsd ?? 0, yearlyPriceUsd: addOn.yearlyPriceUsd ?? 0 })
      : 0;
  const username = tradingViewAccount?.username || null;
  const locked = tradingViewAccount?.locked ?? false;
  const needsUsername = tradingViewAccount !== null && username === null;

  const chooseCycle = (chosen: BillingCycle) => {
    if (cartLine) {
      updateBillingCycle(addOn.id, chosen, addOnPrice(addOn, chosen));
    } else {
      setPendingCycle(chosen);
    }
  };

  const addToCart = () => {
    const cartItem: AddOnCartItem = { addOnId: addOn.id, name: addOn.name, billingCycle: cycle, priceUsd: price };
    addAddOn(cartItem);
    showSuccess(t("buy.added", { addOn: addOn.name }));
  };

  let accountNote: string;
  if (locked) {
    accountNote = t("username.locked");
  } else if (username) {
    accountNote = t("username.locksOnStart");
  } else {
    accountNote = t("username.invite");
  }

  let buyNote: string;
  if (cartHeldBySomethingElse && !cartLine) {
    buyNote = t("buy.cartHeld");
  } else if (needsUsername && !cartLine) {
    buyNote = t("buy.usernameFirst");
  } else if (cycle === "Yearly") {
    buyNote = t("buy.yearlyNote");
  } else {
    buyNote = t("buy.monthlyNote");
  }

  return (
    <div className="d-flex flex-column align-items-stretch gap-3" style={{ minWidth: 260 }}>
      <div className="text-body-secondary text-uppercase small text-sm-end" style={{ letterSpacing: "0.06em" }}>
        {t("buy.cycle")}
      </div>
      {cycles.length > 1 ? (
        <CButtonGroup className="btn-group-pill">
          <CButton
            color="primary"
            variant={cycle === "Monthly" ? undefined : "outline"}
            onClick={() => chooseCycle("Monthly")}
          >
            {t("buy.monthly")}
          </CButton>
          <CButton
            color="primary"
            variant={cycle === "Yearly" ? undefined : "outline"}
            onClick={() => chooseCycle("Yearly")}
          >
            {t("buy.yearly")}
            {savings > 0 && <span className="ms-1 small">{t("buy.save", { percent: savings })}</span>}
          </CButton>
        </CButtonGroup>
      ) : (
        <div className="fw-semibold text-sm-end">{cycle === "Yearly" ? t("buy.yearly") : t("buy.monthly")}</div>
      )}
      <div className="text-sm-end" style={{ fontSize: "2.25rem", fontWeight: 700, lineHeight: 1.1 }}>
        {formatPriceUsd(price)}
        <span className="text-body-secondary" style={{ fontSize: "0.95rem", fontWeight: 400 }}>
          {" "}
          {cycle === "Yearly" ? t("buy.perYear") : t("buy.perMonth")}
        </span>
      </div>
      {tradingViewAccount && (
        <div className="border rounded px-3 py-2 d-flex flex-column gap-1">
          <div className="text-body-secondary text-uppercase small fw-semibold" style={{ letterSpacing: "0.06em" }}>
            {t("detail.tradingViewAccount")}
          </div>
          <div className="d-flex justify-content-between align-items-center gap-2">
            {username ? (
              <span className="d-inline-flex align-items-center gap-2 text-break" style={{ minWidth: 0 }}>
                {locked && <CIcon icon={cilLockLocked} className="text-body-secondary flex-shrink-0" aria-hidden />}
                {username}
              </span>
            ) : (
              <span className="text-body-secondary fst-italic">{t("username.notAdded")}</span>
            )}
            {!locked && (
              <Link
                to={routePaths.profileTradingView}
                state={{ returnTo: addOnDetailsPath(addOn.id) }}
                className="btn btn-outline-primary btn-sm flex-shrink-0"
              >
                {username ? t("username.change") : t("detail.addUsername")}
              </Link>
            )}
          </div>
          <div className="small text-body-secondary">{accountNote}</div>
        </div>
      )}
      {cartLine ? (
        <CButton color="danger" variant="outline" onClick={() => removeAddOn(addOn.id)}>
          {t("buy.remove")}
        </CButton>
      ) : (
        <CButton color="primary" disabled={cartHeldBySomethingElse || needsUsername} onClick={addToCart}>
          <CIcon icon={cilCart} className="me-2" />
          {t("buy.add")}
        </CButton>
      )}
      <div className="small text-body-secondary text-sm-end">{buyNote}</div>
    </div>
  );
}
